// src/components/Room/MultiplacerCeilingDevicesToolBar.tsx
import React, { useState } from "react";
import { Box, Button, Group, Menu, Text, Tooltip } from "@mantine/core";
import { FaCopy, FaDiagramProject } from "react-icons/fa6";
import { FaChevronDown } from "react-icons/fa";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectHttpDevicesInRoom } from "../../DesignTab/devices/deviceSlice";
import { selectMlDevicesAvailableInSelectedSpace } from "../../Room/roomSelectors";
import { FaBrain } from "react-icons/fa";
import { setCopySpaceModeActiveInRedux } from "../../DesignTab/designSlice";
import { selectIsSystemAdmin } from "../../userSelectors";
import {
  addDeviceTypeToSelectedRoomThunk,
  thunkAddMlDevicesToSelectedRoom,
} from "../../DesignTab/devices/deviceThunks";
import { thunkGenerateDemoPipeRoutingForActiveRoom } from "../../DesignTab/routing/routingThunks";
import { DeleteAllDevicesInRoomButton } from "./DeleteAllDevicesInRoomButton";
import { RoomLockToggleButton } from "./RoomLockToggleButton";
import DeviceTypePicker from "./DeviceTypePicker";
import { IS_PROD } from "../../config";
import { FailedAlert } from "../../components/FailedAlert";
import { selectMlCeilingPredicionArgs } from "./mlSelectors";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import CopyFromStandardRoomMenu from "./CopyFromStandardRoomMenu";
import { AdvancedCopyDevicesModal } from "../../DesignTab/devices/SelectedRooms/AdvancedCopyDevices/AdvancedCopyDevicesModal";
import { LiveMlDemoButton } from "./LiveMlDemoButton";
import { GenericDeviceModal } from "./GenericDeviceModal";
import { DeviceCategoryEnum } from "@multiconsult-group/multiplacer-core";

export const MultiplacerCeilingDevicesToolBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const [advancedCopyModalOpen, setAdvancedCopyModalOpen] = useState(false);
  const [genericDeviceCategory, setGenericDeviceCategory] = useState<DeviceCategoryEnum | null>(
    null
  );

  const devicesInRoom = useAppSelector(selectHttpDevicesInRoom);
  const singleRoom = useAppSelector(selectHttpSingleRoomFull);
  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const copySpaceMode = useAppSelector((s) => s.design.copySpaceModeActive);
  const isSystemAdmin = useAppSelector(selectIsSystemAdmin);
  const isLocked = singleRoom?.multiplacerCeilingDevicesLocked?.bool === true;
  const productCount = devicesInRoom?.length ?? 0;

  const hideCopyButton = selectedRooms.length > 1 || productCount === 0;

  const copyTooltip = copySpaceMode
    ? "Velg mellom enkel kopiering på samme etasje eller avansert kopiering på tvers av etasjer. Enkel kopiering er aktiv nå."
    : "Velg mellom enkel kopiering på samme etasje eller avansert kopiering på tvers av etasjer.";

  // Gemensam knapphöjd för en mer polerad look
  const buttonHeight = 30;

  const mlDevicesAvailable = useAppSelector(selectMlDevicesAvailableInSelectedSpace);

  return (
    <Box
      bg="gray.0"
      p="xs"
      mb="xs"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        border: "1px solid var(--mantine-color-gray-3)",
        boxShadow: "var(--mantine-shadow-xs)",
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap={6}>
          <RoomLockToggleButton />

          <DeviceTypePicker
            onSelect={(deviceType) => dispatch(addDeviceTypeToSelectedRoomThunk(deviceType))}
            onSelectGeneric={(deviceCategory) =>
              setGenericDeviceCategory(deviceCategory as DeviceCategoryEnum)
            }
            disabled={isLocked}
          />
          {mlDevicesAvailable && (
            <Tooltip label="Legg inn komponenter fra AI-forslag">
              <Button
                h={buttonHeight}
                size="xs"
                variant="light"
                color="blue"
                leftSection={<FaBrain size={14} />}
                disabled={isLocked}
                radius="md"
                onClick={() => dispatch(thunkAddMlDevicesToSelectedRoom())}
              >
                Bruk AI-forslag
              </Button>
            </Tooltip>
          )}

          {!hideCopyButton && (
            <Menu shadow="md" width={340} position="bottom-start" withinPortal>
              <Menu.Target>
                <Tooltip label={copyTooltip} multiline maw={320}>
                  <Button
                    h={buttonHeight}
                    size="xs"
                    variant={copySpaceMode ? "filled" : "outline"}
                    color="blue"
                    leftSection={<FaCopy size={14} />}
                    rightSection={<FaChevronDown size={10} />}
                    radius="md"
                  >
                    Kopier oppsett
                  </Button>
                </Tooltip>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Kopier oppsett</Menu.Label>
                <Text size="xs" c="dimmed" px="sm" pb="xs">
                  Enkel kopiering brukes for rom på samme etasje. Avansert kopiering brukes når du
                  vil velge målrom på flere etasjer.
                </Text>

                <Menu.Item
                  leftSection={<FaCopy size={14} />}
                  onClick={() => dispatch(setCopySpaceModeActiveInRedux(!copySpaceMode))}
                >
                  {copySpaceMode ? "Avslutt enkel kopiering" : "Enkel kopiering"}
                </Menu.Item>

                <Menu.Item
                  leftSection={<FaCopy size={14} />}
                  onClick={() => setAdvancedCopyModalOpen(true)}
                >
                  Avansert kopiering
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}

          <CopyFromStandardRoomMenu />

          {isSystemAdmin && (
            <Tooltip label="Generer et demo-rør i aktivt rom">
              <Button
                h={buttonHeight}
                size="xs"
                variant="outline"
                leftSection={<FaDiagramProject size={14} />}
                disabled={isLocked}
                radius="md"
                onClick={() => dispatch(thunkGenerateDemoPipeRoutingForActiveRoom())}
              >
                Generer rør
              </Button>
            </Tooltip>
          )}

          {!IS_PROD && (
            <Tooltip label="Kjør live ML-demo for dette rommet (dev-verktøy)">
              <Group gap={6}>
                <LiveMlDemoButton buttonHeight={buttonHeight} disabled={isLocked} />
              </Group>
            </Tooltip>
          )}
          {devicesInRoom.length > 0 && <DeleteAllDevicesInRoomButton />}
        </Group>
      </Group>
      {advancedCopyModalOpen && (
        <AdvancedCopyDevicesModal onClose={() => setAdvancedCopyModalOpen(false)} />
      )}
      {genericDeviceCategory && (
        <GenericDeviceModal
          initialDeviceCategory={genericDeviceCategory}
          onClose={() => setGenericDeviceCategory(null)}
        />
      )}
    </Box>
  );
};
